import { createFileRoute, Link } from "../lib/router-compat";
import { PageShell } from "../components/PageShell";

export const Route = createFileRoute("/history")({
  head: () => ({
    meta: [
      { title: "History of Pan in Maine — The Pan Institute" },
      {
        name: "description",
        content:
          "A chronology of steelpan in Maine from 1974 to today, including Atlantic Clarion, Pan Institute, and the Chase family bands.",
      },
      { property: "og:title", content: "History of Pan in Maine" },
      {
        property: "og:description",
        content:
          "A chronology of steelpan in Maine from 1974 to today.",
      },
    ],
  }),
  component: HistoryPage,
});

type Era = {
  era: string;
  headline: string;
  events: { when: string; title: string; body: string }[];
};

const ERAS: Era[] = [
  {
    era: "1974 – 1979",
    headline: "First pan made in Maine",
    events: [
      { when: "1974", title: "A souvenir from the Caribbean", body: "Carl Chase returns from the Caribbean with a tourist pan for four-year-old Nigel and Pete Seeger's How to Make and Play Steel Drums. He hammers out the first pan made in Maine — a ping-pong — in the backyard on Timothy's Lane, Brooksville." },
      { when: "1974", title: "A band in the boathouse", body: "Carl makes a single 2nd and pulls Butch Czerwinski in to play it. By spring there are guitars, cellos and basses in the Golden Stairs boathouse on Betsy's Cove." },
      { when: "Winter 1975", title: "Atlantic Clarion is named", body: "Practicing in Sarah Christie's kitchen, the band takes its name from her wood-burning range — Atlantic Clarion Steel Band." },
      { when: "1976", title: "Bicentennial parade", body: "ACSB performs on a trailer in the Brooksville Bicentennial parade." },
      { when: "ca. 1977", title: "Monday Nights are born", body: "ACSB begins summer street dances on the steps of the Brooksville Post Office. Loved by schooner crews and back-to-the-landers, hated by Route 176 traffic — eventually shut down by the selectmen." },
    ],
  },
  {
    era: "1980 – 1989",
    headline: "Winters in Woods Hole, summers on the peninsula",
    events: [
      { when: "1980 – 89", title: "Two coasts, one band", body: "Carl, family and ACSB winter in Woods Hole, MA, returning for summer gigs on the peninsula — including afternoons outside the newly opened Firepond in Blue Hill." },
      { when: "Winter 1987", title: "Trinidad & Roland Harrigin", body: "Carl, Butch and families travel to Trinidad for Carnival, meet master tuner Roland Harrigin, and start learning the real craft of building pans." },
    ],
  },
  {
    era: "1990 – 1999",
    headline: "From Bucks Harbor to Boston Panorama",
    events: [
      { when: "1990", title: "Citronella Steel", body: "Butch buys the Bucks Harbor Market. Carl advertises in the Weekly Packet for anyone wanting to learn pan — a huge turnout becomes Citronella Steel, taught behind the market." },
      { when: "Spring 1991", title: "PanNE Festival at UNH", body: "Eight New England steelbands gather, including Bagaduce Steel, Panik (Nigel, UNH) and Panacea (Carl, Cape Cod Academy). Citronella moves to GSA Adult Ed and becomes FLASH! in the PANS — Monday nights resume outside Bucks Harbor Market." },
      { when: "Spring 1992", title: "Rhythm Rockets & Planet Pan", body: "Nigel returns from playing with Renegades in Trinidad, starts Rhythm Rockets and the teenage group that would become Planet Pan. Carl is hired to teach steelband at GSA." },
      { when: "Summer 1994", title: "Back to Blue Hill Town Park", body: "Monday night street dances begin again, this time in Blue Hill Town Park." },
      { when: "Fall 1994", title: "Heavenly Fire", body: "Nigel leaves for Boston to start Heavenly Fire at St. Mary's Episcopal. Carl takes over Planet Pan, which travels to the Big E." },
      { when: "May 1995", title: "Peninsula Pan Festival", body: "Peninsula Pan hosts a festival in Blue Hill with Ron Reid from Trinidad." },
      { when: "Aug 1997", title: "Boston Panorama", body: "Pan Coalition plays Guitar Pan at Boston Panorama." },
      { when: "Spring 1999", title: "Carl leaves GSA", body: "Carl's employment at GSA ends over his objections to the fingerprint law." },
    ],
  },
  {
    era: "2000 – 2009",
    headline: "Peninsula Pan, Pan Institute, Montreal",
    events: [
      { when: "Aug 2000", title: "Boston Panorama — 2nd place", body: "Pan Coalition plays Backyard Party and takes second." },
      { when: "Fall 2000", title: "Peninsula Pan begins", body: "Flash starts the process of becoming a 501(c)(3); Planet Pan continues unsupported." },
      { when: "Spring 2001", title: "Peninsula Pan incorporated", body: "Begins paying teachers for youth and adult programs." },
      { when: "May 2001", title: "Samaroo Jets", body: "Pan Festival featuring Samaroo Jets from Trinidad." },
      { when: "Fall 2003", title: "Nigel returns to GSA", body: "Takes over GSA Pan Classes, Planet Pan, Rhythm Rockets, Pan Pirates and Adult Classes." },
      { when: "Jul 2004", title: "Montreal", body: "Pan Coalition plays Toco Band and It Doh Mean A Ting." },
      { when: "Fall 2005", title: "Pan Institute is born", body: "Peninsula Pan Inc. hands over all educational programs to Nigel's new Pan Institute." },
      { when: "Jul 2005", title: "Montreal", body: "From Beyond and Copacabana." },
      { when: "Jul 2006", title: "Montreal — 1st place", body: "Colors Again and Don't You Worry — first place." },
      { when: "Jul 2007", title: "ACSB to Nigel", body: "Carl gives ownership of ACSB to Nigel. Pan Coalition plays Band from Space in Montreal." },
      { when: "May 2008", title: "PanNE in Blue Hill", body: "Nigel directs the Pan New England Festival with Steel Sensation from Queens, NY." },
      { when: "Jul 2008", title: "Toronto / Caribana", body: "Pan Coalition performs with the Canadian armed forces at Caribana." },
      { when: "Jul 2009", title: "Montreal", body: "Hooked and September." },
      { when: "Fall 2009", title: "Nigel directs Flash", body: "Carl steps down; Nigel is hired to direct Flash." },
    ],
  },
  {
    era: "2010 – 2019",
    headline: "Touring years",
    events: [
      { when: "Jul 2010", title: "Montreal — combined band", body: "All of Flash with other bands play FLASH and Copacabana II. GSA dedicates the yearbook to Nigel." },
      { when: "Fall 2010", title: "Carl returns", body: "Carl returns to direct Flash." },
      { when: "2011 – 14", title: "Four Montreals in a row", body: "Pan Is Calling Meh / Hips Don't Lie ('11), More Vibes / Hungarian Dance ('12), Endless Vibes / This Feeling Nice ('13), Spankin ('14)." },
      { when: "Aug 2015", title: "Trinidad — World Steelband Conference", body: "Pan Coalition travels with all expenses paid for 40 players to Trinidad for the World Steelband Conference and Panorama." },
      { when: "Fall 2015", title: "Kollman directs Flash", body: "Carl resigns again; John Kollman is hired." },
      { when: "Fall 2017", title: "Aaron Copland School", body: "Nigel is accepted to study Orchestral Conducting at the Aaron Copland School of Music." },
      { when: "2017 – 19", title: "Fiona joins", body: "Fiona is hired to teach after-school and to direct Flash, with John assisting." },
      { when: "Fall 2019", title: "Carl and Susan pass away", body: "The peninsula loses two of its founders." },
    ],
  },
  {
    era: "2020 – Today",
    headline: "Pandemic, parting, and a new home",
    events: [
      { when: "Winter 2020", title: "Pandemic", body: "No street dances in the park that summer. Nigel returns from NYC and organizes Pandemic Steel for all ages in the GSA courtyard." },
      { when: "Fall 2020", title: "Tim Sepe directs Flash", body: "No pan classes through 2020 – 21; ACSB keeps practicing in Brooksville." },
      { when: "Summer 2021", title: "Reunion in the park", body: "ACSB, Planet Pan and Flash all play Monday Nights together in Blue Hill Town Park." },
      { when: "Spring 2022", title: "The split", body: "Flash decides Pan Institute bands can no longer perform with them on Monday Nights. Tim Sepe quits; Martin Conte is hired and convinces GSA to order its own pans. Pan Institute's bands are told they are no longer welcome at GSA." },
      { when: "Summer 2022", title: "Monday Nights! in the Park", body: "Nigel organizes a new community concert series — ACSB, Planet Pan, Rhythm Rockets and Pan Coalition. Flash continues to play in the park separately." },
      { when: "Fall 2022", title: "Move to Bagaduce Music", body: "Pan Institute Classes relocate to Bagaduce Music. Carl's arrangements and compositions go to the Bagaduce Lending Library." },
      { when: "Spring 2023", title: "30th PanNE Festival", body: "Hosted at Bagaduce in honor of Carl and Susan Chase." },
      { when: "Summer 2023", title: "NY Panorama", body: "Pan Coalition performs Engine Room with Steel Sensation in Brooklyn." },
      { when: "2024", title: "ACSB turns 50", body: "Atlantic Clarion celebrates 50 years of performing, teaching and making pans in Brooksville and beyond." },
      { when: "May 2024", title: "PanNE in Augusta", body: "Festival at Cony High School with Steel Sensation as guest artists. Pan Institute brings Nigel and the Believer." },
      { when: "Fall 2024", title: "NY Panorama", body: "Pan Coalition returns to NY with Steel Sensation playing Look How We Liven." },
      { when: "Jan 20, 2025", title: "A new home opens", body: "Grand opening of the new Pan facility." },
    ],
  },
];

function HistoryPage() {
  const totalEvents = ERAS.reduce((n, e) => n + e.events.length, 0);
  return (
    <PageShell
      tag=""
      title={<>Pan on the <span className="text-primary">peninsula</span>.</>}
      intro="Half a century of steelpan in Maine — every band, every parade, every Monday night — from a backyard ping-pong in Brooksville to international panoramas."
    >
      {/* Stat strip */}
      <dl className="grid grid-cols-3 divide-x divide-border/40 rounded-2xl border border-border/40 bg-card/30">
        <div className="px-2 sm:px-4 py-6 text-center min-w-0">
          <dt className="font-display italic text-2xl sm:text-3xl md:text-5xl text-primary leading-none">50+</dt>
          <dd className="mt-2 text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.35em] text-foreground/55 break-words">Years of pan</dd>
        </div>
        <div className="px-2 sm:px-4 py-6 text-center min-w-0">
          <dt className="font-display italic text-2xl sm:text-3xl md:text-5xl text-primary leading-none">{ERAS.length}</dt>
          <dd className="mt-2 text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.35em] text-foreground/55 break-words">Eras</dd>
        </div>
        <div className="px-2 sm:px-4 py-6 text-center min-w-0">
          <dt className="font-display italic text-2xl sm:text-3xl md:text-5xl text-primary leading-none">{totalEvents}</dt>
          <dd className="mt-2 text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.35em] text-foreground/55 break-words">Milestones</dd>
        </div>
      </dl>

      {/* Era jump nav */}
      <nav className="mt-10 flex flex-wrap gap-2" aria-label="Eras">
        {ERAS.map((era) => {
          const id = `era-${era.era.replace(/\s+/g, "")}`;
          return (
            <a
              key={era.era}
              href={`#${id}`}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(id);
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                  history.replaceState(null, "", `#${id}`);
                }
              }}
              className="rounded-full border border-border/50 bg-card/30 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] text-foreground/70 hover:text-foreground hover:border-primary/60 transition"
            >
              {era.era}
            </a>
          );
        })}
      </nav>

      {/* Timeline */}
      <div className="mt-20 space-y-28">
        {ERAS.map((era) => (
          <section key={era.era} id={`era-${era.era.replace(/\s+/g, "")}`} className="relative scroll-mt-24">
            <header className="grid md:grid-cols-12 gap-6 items-end mb-12 border-b border-border/40 pb-8">
              <div className="md:col-span-12">
                <div className="text-[10px] uppercase tracking-[0.5em] text-primary">{era.era}</div>
                <h2 className="mt-3 font-display italic text-3xl md:text-5xl leading-[1.05]">
                  {era.headline}
                </h2>
              </div>
            </header>

            <ol className="relative border-l border-border/40 pl-8 ml-2 space-y-12">
              {era.events.map((e, i) => (
                <li key={i} className="relative group">
                  <span className="absolute -left-[39px] top-2 h-3 w-3 rounded-full bg-primary border-2 border-background shadow-[0_0_0_4px_var(--background)] group-hover:scale-125 transition-transform" />
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className="text-[10px] uppercase tracking-[0.4em] text-primary/80">
                      {e.when}
                    </div>
                    <span className="h-px flex-1 bg-border/40 min-w-[40px]" />
                  </div>
                  <h3 className="mt-2 font-display italic text-2xl md:text-3xl leading-tight">
                    {e.title}
                  </h3>
                  <p className="mt-3 text-sm md:text-[15px] text-foreground/65 leading-relaxed max-w-2xl">
                    {e.body}
                  </p>
                </li>
              ))}
            </ol>
          </section>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-24 rounded-3xl border border-border/40 bg-gradient-to-br from-primary/15 via-card/40 to-transparent p-8 md:p-12">
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">Add to the record</div>
            <h2 className="mt-3 font-display italic text-3xl md:text-4xl leading-[1.05]">
              Have a story, a date, or a photo we missed?
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-7 py-3 bg-primary text-primary-foreground text-[11px] tracking-[0.3em] uppercase rounded-full hover:opacity-90 transition shadow-lg shadow-primary/20 whitespace-nowrap"
            >
              Send us your story
            </Link>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
